<template>
	<div
		ref="groupRef"
		class="segmented-switch"
		:class="[`is-moving-${direction}`, { 'is-animating': animating }]"
		role="tablist"
		:aria-label="label"
	>
		<span class="segmented-switch-thumb" :style="thumbStyle" aria-hidden="true"></span>
		<button
			v-for="option in options"
			:id="tabId ? tabId(option.value) : undefined"
			:key="option.value"
			:ref="(element) => setButton(option.value, element)"
			type="button"
			role="tab"
			class="segmented-switch-option"
			:class="{ active: option.value === modelValue }"
			:aria-selected="option.value === modelValue ? 'true' : 'false'"
			:aria-controls="panelId ? panelId(option.value) : undefined"
			@click="choose(option.value)"
		>
			<slot name="option" :option="option">{{ option.label }}</slot>
		</button>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

// The segmented switch from the Payments page; the thumb flows between options like
// the Appointments Journal/Summary switch, but only when an option is clicked.
const props = defineProps({
	modelValue: { type: String, default: '' },
	// [{ value, label }]
	options: { type: Array, required: true },
	label: { type: String, default: '' },
	tabId: { type: Function, default: null },
	panelId: { type: Function, default: null },
});

const emit = defineEmits(['update:modelValue']);

const groupRef = ref(null);
const buttons = new Map();
const thumb = ref({ left: 0, right: 0, visible: false });
const animating = ref(false);
const direction = ref('right');
let observer;
let settleTimer;

const setButton = (value, element) => {
	if (element) buttons.set(value, element);
	else buttons.delete(value);
};

const thumbStyle = computed(() => ({
	left: `${thumb.value.left}px`,
	right: `${thumb.value.right}px`,
	opacity: thumb.value.visible ? 1 : 0,
}));

// Both edges are placed separately, so they can travel at slightly different times.
const measure = () => {
	const group = groupRef.value;
	const button = buttons.get(props.modelValue);
	if (!group || !button) {
		thumb.value = { ...thumb.value, visible: false };
		return;
	}
	thumb.value = {
		left: button.offsetLeft,
		right: group.clientWidth - button.offsetLeft - button.offsetWidth,
		visible: true,
	};
};

const indexOf = (value) => props.options.findIndex((option) => option.value === value);

const choose = (value) => {
	if (value === props.modelValue) return;
	direction.value = indexOf(value) > indexOf(props.modelValue) ? 'right' : 'left';
	animating.value = true;
	clearTimeout(settleTimer);
	settleTimer = setTimeout(() => {
		animating.value = false;
	}, 520);
	emit('update:modelValue', value);
};

watch(() => [props.modelValue, props.options.length], () => nextTick(measure), { flush: 'post' });

onMounted(() => {
	measure();
	if (typeof ResizeObserver !== 'undefined') {
		observer = new ResizeObserver(measure);
		observer.observe(groupRef.value);
	}
});

onBeforeUnmount(() => {
	observer?.disconnect();
	clearTimeout(settleTimer);
});
</script>

<style scoped>
.segmented-switch {
	--switch-flow: cubic-bezier(0.45, 0, 0.2, 1);
	position: relative;
	display: inline-flex;
	/* Never stretched by a column layout around it. */
	align-self: flex-start;
	align-items: center;
	gap: 4px;
	max-width: 100%;
	padding: 2px;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	box-sizing: border-box;
}

.segmented-switch-thumb {
	position: absolute;
	top: 2px;
	bottom: 2px;
	border-radius: 6px;
	background: var(--noc-blue-05, rgba(10, 97, 201, 0.05));
	pointer-events: none;
}

/* The leading edge moves first and the trailing edge follows, without overshooting. */
.segmented-switch.is-animating .segmented-switch-thumb {
	transition:
		left 380ms var(--switch-flow),
		right 380ms var(--switch-flow);
}

.segmented-switch.is-animating.is-moving-right .segmented-switch-thumb {
	transition-delay: 80ms, 0ms;
}

.segmented-switch.is-animating.is-moving-left .segmented-switch-thumb {
	transition-delay: 0ms, 80ms;
}

.segmented-switch-option {
	position: relative;
	z-index: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	min-height: 30px;
	padding: 0 18px;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color-secondary);
	font: var(--ap-type-control, 500 0.875rem/1.25rem var(--font-family-base));
	white-space: nowrap;
	cursor: pointer;
	transition: background-color 150ms ease;
}

.segmented-switch-option.active {
	color: var(--noc-blue);
}

.segmented-switch-option:focus-visible {
	outline: 2px solid var(--noc-blue);
	outline-offset: 2px;
}

/* The label turns blue as the thumb arrives underneath it. */
.segmented-switch.is-animating .segmented-switch-option {
	transition:
		color 180ms ease,
		background-color 150ms ease;
}

.segmented-switch.is-animating .segmented-switch-option.active {
	transition:
		color 180ms ease 120ms,
		background-color 150ms ease;
}

@media (hover: hover) {
	.segmented-switch-option:not(.active):hover {
		background: #f5f6f8;
	}
}

@media (max-width: 650px) {
	.segmented-switch-option {
		padding-inline: 12px;
	}
}

@media (prefers-reduced-motion: reduce) {
	.segmented-switch.is-animating .segmented-switch-thumb,
	.segmented-switch.is-animating .segmented-switch-option,
	.segmented-switch.is-animating .segmented-switch-option.active {
		transition: none;
	}
}
</style>
